import { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { Loader2, CheckCircle2, KeyRound, ShieldCheck, MapPin } from "lucide-react";

const BASE = process.env.REACT_APP_BACKEND_URL;

export default function RichiediCredenziali() {
  const { buildingId } = useParams();
  const [nome, setNome] = useState("");
  const [cognome, setCognome] = useState("");
  const [email, setEmail] = useState("");
  const [condominio, setCondominio] = useState("");
  const [indirizzo, setIndirizzo] = useState("");
  const [studio, setStudio] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");
  const [notFound, setNotFound] = useState(false);

  // Autocompletamento indirizzo (Google Places — proxy backend pubblico)
  const [addrResults, setAddrResults] = useState([]);
  const [addrOpen, setAddrOpen] = useState(false);
  const [addrLoading, setAddrLoading] = useState(false);
  const debRef = useRef(null);
  const boxRef = useRef(null);
  const sessRef = useRef((window.crypto?.randomUUID?.() || String(Math.random())));

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${BASE}/api/public/credential-request/building/${buildingId}`);
        if (!r.ok) { setNotFound(true); return; }
        const d = await r.json();
        setCondominio(d.condominio || "");
        setIndirizzo(d.indirizzo || "");
        setStudio(d.studio || "");
      } catch { setNotFound(true); }
    })();
  }, [buildingId]);

  useEffect(() => {
    const onClick = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setAddrOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const onIndirizzoChange = (val) => {
    setIndirizzo(val);
    if (debRef.current) clearTimeout(debRef.current);
    if (!val || val.trim().length < 3) { setAddrResults([]); setAddrOpen(false); return; }
    debRef.current = setTimeout(async () => {
      setAddrLoading(true);
      try {
        const r = await fetch(`${BASE}/api/public/geocode/autocomplete?q=${encodeURIComponent(val)}&session_token=${sessRef.current}`);
        const d = await r.json();
        setAddrResults(d?.suggestions || []); setAddrOpen(true);
      } catch { setAddrResults([]); }
      finally { setAddrLoading(false); }
    }, 300);
  };

  const pickAddress = async (s) => {
    setAddrOpen(false);
    let text = s.text || s.main_text || "";
    try {
      const r = await fetch(`${BASE}/api/public/geocode/details/${s.place_id}?session_token=${sessRef.current}`);
      const d = await r.json();
      text = d?.formatted_address || text;
    } catch { /* usa il testo del suggerimento */ }
    setIndirizzo(text);
    sessRef.current = window.crypto?.randomUUID?.() || String(Math.random());
  };

  const submit = async () => {
    setErr("");
    if (!nome.trim() || !cognome.trim() || !email.trim() || !condominio.trim()) {
      setErr("Compila tutti i campi obbligatori.");
      return;
    }
    if (!/^\S+@\S+\.\S+$/.test(email.trim())) { setErr("Inserisci un'email valida."); return; }
    setBusy(true);
    try {
      const r = await fetch(`${BASE}/api/public/credential-request`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ building_id: buildingId, nome: nome.trim(), cognome: cognome.trim(), email: email.trim(), condominio: condominio.trim(), indirizzo: indirizzo.trim() }),
      });
      if (!r.ok) { const d = await r.json().catch(() => ({})); setErr(d.detail || "Invio non riuscito."); return; }
      setDone(true);
    } catch { setErr("Errore di connessione. Riprova più tardi."); }
    finally { setBusy(false); }
  };

  const inputCls = "w-full mb-3 bg-secondary border border-border rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#007BFF]";

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col items-center justify-center px-4 py-10" data-testid="richiedi-credenziali">
      <div className="flex items-center gap-2 mb-8">
        <img src="/sedio-logo.png" alt="Sedio" className="h-8 w-auto object-contain" />
        <span className="font-head text-xl font-extrabold tracking-tight">Sedio</span>
      </div>
      <div className="w-full max-w-sm bg-card border border-border rounded-2xl p-6">
        {notFound ? (
          <div className="text-center" data-testid="cred-notfound">
            <h1 className="font-head text-xl font-extrabold tracking-tight">Link non valido</h1>
            <p className="text-sm text-muted-foreground mt-2">Il link di richiesta credenziali non è valido o è scaduto. Contatta l'amministrazione.</p>
          </div>
        ) : done ? (
          <div className="text-center" data-testid="cred-done">
            <div className="flex justify-center mb-3"><CheckCircle2 className="h-10 w-10 text-green-600" /></div>
            <h1 className="font-head text-xl font-extrabold tracking-tight">Richiesta inviata!</h1>
            <p className="text-sm text-muted-foreground mt-2">
              La tua richiesta è stata inoltrata all'amministrazione{studio ? ` (${studio})` : ""}. Riceverai le credenziali di accesso al portale via email appena approvata.
            </p>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2 mb-1">
              <KeyRound className="h-5 w-5 text-[#007BFF]" />
              <h1 className="font-head text-xl font-extrabold tracking-tight">Richiedi le credenziali</h1>
            </div>
            <p className="text-sm text-muted-foreground mt-1 mb-5">
              Compila il modulo per richiedere l'accesso al portale condòmino{studio ? ` gestito da ${studio}` : ""}.
            </p>
            <label className="text-[11px] uppercase tracking-wide text-muted-foreground">Nome *</label>
            <input data-testid="cred-nome" value={nome} onChange={(e) => setNome(e.target.value)} placeholder="Nome" className={`${inputCls} mt-1`} />
            <label className="text-[11px] uppercase tracking-wide text-muted-foreground">Cognome *</label>
            <input data-testid="cred-cognome" value={cognome} onChange={(e) => setCognome(e.target.value)} placeholder="Cognome" className={`${inputCls} mt-1`} />
            <label className="text-[11px] uppercase tracking-wide text-muted-foreground">Email *</label>
            <input data-testid="cred-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="La tua email" className={`${inputCls} mt-1`} />
            <label className="text-[11px] uppercase tracking-wide text-muted-foreground">Nome del condominio *</label>
            <input data-testid="cred-condominio" value={condominio} onChange={(e) => setCondominio(e.target.value)} placeholder="Nome del condominio" className={`${inputCls} mt-1`} />
            <label className="text-[11px] uppercase tracking-wide text-muted-foreground">Indirizzo del condominio</label>
            <div className="relative mt-1 mb-3" ref={boxRef}>
              <MapPin className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
              <input data-testid="cred-indirizzo" value={indirizzo}
                onChange={(e) => onIndirizzoChange(e.target.value)}
                onFocus={() => { if (addrResults.length) setAddrOpen(true); }}
                autoComplete="off" placeholder="Inizia a digitare l'indirizzo…"
                className={`${inputCls} !mb-0 pl-9 pr-9`} />
              {addrLoading && <Loader2 className="h-4 w-4 absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground animate-spin" />}
              {addrOpen && addrResults.length > 0 && (
                <div data-testid="cred-indirizzo-suggestions" className="absolute z-20 left-0 right-0 mt-1 bg-card border border-border rounded-lg shadow-lg overflow-hidden max-h-60 overflow-y-auto">
                  {addrResults.map((s) => (
                    <button key={s.place_id} type="button" data-testid={`cred-addr-opt-${s.place_id}`}
                      onClick={() => pickAddress(s)}
                      className="w-full text-left px-3 py-2 hover:bg-secondary transition-colors flex items-start gap-2">
                      <MapPin className="h-3.5 w-3.5 mt-0.5 text-[#007BFF] shrink-0" />
                      <span className="min-w-0">
                        <span className="block text-sm truncate">{s.main_text || s.text}</span>
                        {s.secondary_text && <span className="block text-[11px] text-muted-foreground truncate">{s.secondary_text}</span>}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            {err && <p className="text-red-500 text-xs mb-3" data-testid="cred-error">{err}</p>}
            <button data-testid="cred-submit" onClick={submit} disabled={busy}
              className="w-full bg-[#007BFF] hover:bg-[#0069D9] text-white rounded-lg py-2.5 text-sm font-semibold flex items-center justify-center disabled:opacity-50">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Invia richiesta"}
            </button>
            <p className="text-[11px] text-muted-foreground/70 mt-4 flex items-center gap-1.5">
              <ShieldCheck className="h-3.5 w-3.5" />I tuoi dati saranno usati solo per creare l'accesso al portale.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
